import { Handshake, Ticket, User } from 'lucide-react';
import {
  ActionTemplate,
  ActionTemplateCategory,
} from '@/Domain/components/Action/actionTemplate.types';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';

export type HubSpotObjectCategory = 'contacts' | 'deals' | 'tickets';

export type HubSpotTemplate = ActionTemplate & { category: HubSpotObjectCategory };

export type HubSpotCategory = ActionTemplateCategory<HubSpotObjectCategory>;

const HUBSPOT_AUTH_HEADERS = [
  { name: 'Authorization', value: 'Bearer {{hubspot_access_token}}' },
  { name: 'Content-Type', value: 'application/json' },
];

export const HUBSPOT_CATEGORIES: HubSpotCategory[] = [
  { id: 'contacts', icon: User, labelKey: 'hubspot_templates.category.contacts' },
  { id: 'deals', icon: Handshake, labelKey: 'hubspot_templates.category.deals' },
  { id: 'tickets', icon: Ticket, labelKey: 'hubspot_templates.category.tickets' },
];

export const HUBSPOT_TEMPLATES: HubSpotTemplate[] = [
  // Contacts
  {
    id: 'create_contact',
    category: 'contacts',
    nameKey: 'hubspot_templates.template.create_contact.name',
    descriptionKey: 'hubspot_templates.template.create_contact.description',
    aiDescription:
      "Use this action to create a new contact in HubSpot CRM. Collect the contact's email, first name, last name, and optionally phone number from the user before making the request.",
    thinkingMessage: 'Creating contact in HubSpot...',
    method: HttpMethodEnum.POST,
    url: 'https://api.hubapi.com/crm/v3/objects/contacts',
    headers: HUBSPOT_AUTH_HEADERS,
    bodyJson: JSON.stringify(
      {
        properties: {
          email: '{{email}}',
          firstname: '{{firstname}}',
          lastname: '{{lastname}}',
          phone: '{{phone}}',
        },
      },
      null,
      2,
    ),
    queryItems: [],
    parameters: [
      {
        name: 'email',
        description: 'Email address of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'firstname',
        description: 'First name of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'lastname',
        description: 'Last name of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'phone',
        description: 'Phone number of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
    ],
  },
  {
    id: 'update_contact',
    category: 'contacts',
    nameKey: 'hubspot_templates.template.update_contact.name',
    descriptionKey: 'hubspot_templates.template.update_contact.description',
    aiDescription:
      "Use this action to update an existing contact in HubSpot CRM. You need the contact's ID and the fields to update.",
    thinkingMessage: 'Updating contact in HubSpot...',
    method: HttpMethodEnum.PATCH,
    url: 'https://api.hubapi.com/crm/v3/objects/contacts/{{contact_id}}',
    headers: HUBSPOT_AUTH_HEADERS,
    bodyJson: JSON.stringify(
      {
        properties: {
          email: '{{email}}',
          firstname: '{{firstname}}',
          lastname: '{{lastname}}',
          phone: '{{phone}}',
        },
      },
      null,
      2,
    ),
    queryItems: [],
    parameters: [
      {
        name: 'contact_id',
        description: 'HubSpot contact ID to update',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'email',
        description: 'Email address of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'firstname',
        description: 'First name of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'lastname',
        description: 'Last name of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'phone',
        description: 'Phone number of the contact',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
    ],
  },
  {
    id: 'search_contacts',
    category: 'contacts',
    nameKey: 'hubspot_templates.template.search_contacts.name',
    descriptionKey: 'hubspot_templates.template.search_contacts.description',
    aiDescription:
      'Use this action to search for contacts in HubSpot CRM. Search by email address or other properties to find matching contacts.',
    thinkingMessage: 'Searching contacts in HubSpot...',
    method: HttpMethodEnum.POST,
    url: 'https://api.hubapi.com/crm/v3/objects/contacts/search',
    headers: HUBSPOT_AUTH_HEADERS,
    bodyJson: JSON.stringify(
      {
        filterGroups: [
          {
            filters: [
              {
                propertyName: '{{search_property}}',
                operator: 'EQ',
                value: '{{search_value}}',
              },
            ],
          },
        ],
        properties: ['firstname', 'lastname', 'email', 'phone'],
      },
      null,
      2,
    ),
    queryItems: [],
    parameters: [
      {
        name: 'search_value',
        description: 'The value to search for (e.g., an email address)',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'search_property',
        description: 'The HubSpot property to search by',
        type: ParameterTypeEnum.String,
        default_value: 'email',
        is_required: false,
      },
    ],
  },

  // Deals
  {
    id: 'create_deal',
    category: 'deals',
    nameKey: 'hubspot_templates.template.create_deal.name',
    descriptionKey: 'hubspot_templates.template.create_deal.description',
    aiDescription:
      'Use this action to create a new deal in HubSpot CRM. Collect the deal name, amount, stage, and pipeline from the user.',
    thinkingMessage: 'Creating deal in HubSpot...',
    method: HttpMethodEnum.POST,
    url: 'https://api.hubapi.com/crm/v3/objects/deals',
    headers: HUBSPOT_AUTH_HEADERS,
    bodyJson: JSON.stringify(
      {
        properties: {
          dealname: '{{dealname}}',
          amount: '{{amount}}',
          dealstage: '{{dealstage}}',
          pipeline: '{{pipeline}}',
        },
      },
      null,
      2,
    ),
    queryItems: [],
    parameters: [
      {
        name: 'dealname',
        description: 'Name of the deal',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'amount',
        description: 'Monetary value of the deal',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'dealstage',
        description: 'Stage of the deal in the pipeline',
        type: ParameterTypeEnum.String,
        default_value: 'appointmentscheduled',
        is_required: false,
      },
      {
        name: 'pipeline',
        description: 'Pipeline the deal belongs to',
        type: ParameterTypeEnum.String,
        default_value: 'default',
        is_required: false,
      },
    ],
  },
  {
    id: 'update_deal',
    category: 'deals',
    nameKey: 'hubspot_templates.template.update_deal.name',
    descriptionKey: 'hubspot_templates.template.update_deal.description',
    aiDescription:
      'Use this action to update an existing deal in HubSpot CRM. You need the deal ID and the fields to update.',
    thinkingMessage: 'Updating deal in HubSpot...',
    method: HttpMethodEnum.PATCH,
    url: 'https://api.hubapi.com/crm/v3/objects/deals/{{deal_id}}',
    headers: HUBSPOT_AUTH_HEADERS,
    bodyJson: JSON.stringify(
      {
        properties: {
          dealname: '{{dealname}}',
          amount: '{{amount}}',
          dealstage: '{{dealstage}}',
        },
      },
      null,
      2,
    ),
    queryItems: [],
    parameters: [
      {
        name: 'deal_id',
        description: 'HubSpot deal ID to update',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'dealname',
        description: 'Name of the deal',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'amount',
        description: 'Monetary value of the deal',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'dealstage',
        description: 'Stage of the deal in the pipeline',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
    ],
  },

  // Tickets
  {
    id: 'create_ticket',
    category: 'tickets',
    nameKey: 'hubspot_templates.template.create_ticket.name',
    descriptionKey: 'hubspot_templates.template.create_ticket.description',
    aiDescription:
      'Use this action to create a new support ticket in HubSpot. Collect the ticket subject and description from the user.',
    thinkingMessage: 'Creating ticket in HubSpot...',
    method: HttpMethodEnum.POST,
    url: 'https://api.hubapi.com/crm/v3/objects/tickets',
    headers: HUBSPOT_AUTH_HEADERS,
    bodyJson: JSON.stringify(
      {
        properties: {
          subject: '{{subject}}',
          content: '{{content}}',
          hs_pipeline: '{{hs_pipeline}}',
          hs_pipeline_stage: '{{hs_pipeline_stage}}',
        },
      },
      null,
      2,
    ),
    queryItems: [],
    parameters: [
      {
        name: 'subject',
        description: 'Subject of the ticket',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: true,
      },
      {
        name: 'content',
        description: 'Description or body of the ticket',
        type: ParameterTypeEnum.String,
        default_value: '',
        is_required: false,
      },
      {
        name: 'hs_pipeline',
        description: 'Pipeline ID for the ticket',
        type: ParameterTypeEnum.String,
        default_value: '0',
        is_required: false,
      },
      {
        name: 'hs_pipeline_stage',
        description: 'Pipeline stage ID for the ticket',
        type: ParameterTypeEnum.String,
        default_value: '1',
        is_required: false,
      },
    ],
  },
];

export const HUBSPOT_SCRATCH_TEMPLATE: HubSpotTemplate = {
  id: 'scratch',
  category: 'contacts',
  nameKey: 'hubspot_templates.start_from_scratch',
  descriptionKey: 'hubspot_templates.start_from_scratch_description',
  aiDescription: '',
  thinkingMessage: '',
  method: HttpMethodEnum.GET,
  url: '',
  headers: HUBSPOT_AUTH_HEADERS,
  bodyJson: JSON.stringify({}, null, 2),
  queryItems: [],
  parameters: [],
};
